import { DATE_GROUP, DATE_TIME_GROUP } from '@constants/common';
import * as am4core from '@amcharts/amcharts4/core';
import { Dayjs, ManipulateType } from 'dayjs';
import dayjs from '@utils/dayjs';
import { DateRange } from '@DStypes/chart';
import { Dispatch, SetStateAction } from 'react';
import { ChartConfigData } from '@api/chart';
import { DataState } from '@pages/EditChart/slice';
import {
  ColumnItemDynamicFilter,
  ColumnItemList,
  ColumnItemRange,
  DynamicFilterTypes,
  FilterType
} from '@DStypes/filter';
import { getUTCTime } from './common';

am4core.addLicense('CH396873293');

export const getBaseInterval = (dateGroup: any, forcedCount = 1) => {
  switch (dateGroup) {
    case DATE_TIME_GROUP.SECONDS:
      return {
        timeUnit: 'second',
        count: forcedCount
      };
    case DATE_TIME_GROUP.MINUTES:
      return {
        timeUnit: 'minute',
        count: forcedCount
      };
    case DATE_TIME_GROUP.HOURS:
      return {
        timeUnit: 'hour',
        count: forcedCount
      };

    case DATE_TIME_GROUP.DAYS:
      return {
        timeUnit: 'day',
        count: forcedCount
      };
    case DATE_TIME_GROUP.WEEKS:
      return {
        timeUnit: 'week',
        count: forcedCount
      };
    case DATE_TIME_GROUP.MONTHS:
      return {
        timeUnit: 'month',
        count: forcedCount
      };
    case DATE_TIME_GROUP.QUARTERS:
      return {
        timeUnit: 'month',
        count: forcedCount * 3
      };
    case DATE_TIME_GROUP.YEARS:
      return {
        timeUnit: 'year',
        count: forcedCount
      };
    case DATE_TIME_GROUP.DECADES:
      return {
        timeUnit: 'year',
        count: 10
      };
    case DATE_TIME_GROUP.CENTURIES:
      return {
        timeUnit: 'year',
        count: 100
      };
  }

  return {};
};

export const getDateGroupOrderDiff = (
  currentDateGroup: DATE_TIME_GROUP,
  dateGroupToCompare: DATE_TIME_GROUP
) => {
  const dateTimeGroupArr = Object.values(DATE_TIME_GROUP);

  if (!dateGroupToCompare) return -1;

  return (
    dateTimeGroupArr.indexOf(currentDateGroup) -
    dateTimeGroupArr.indexOf(dateGroupToCompare)
  );
};

export const getStartOfCentury = (year: any) => {
  const centuryStartYear = Math.floor(year / 100) * 100;
  const centuryStartDate = dayjs(`${centuryStartYear}-01-01`).startOf('year');

  return centuryStartDate;
};

export const getStartOfDecade = (year: any) => {
  const decadeStartYear = Math.floor(year / 10) * 10;
  const decadeStartDate = dayjs(`${decadeStartYear}-01-01`).startOf('year');

  return decadeStartDate;
};

export const getDateDiff = (
  dateGroup: any,
  startDate: Dayjs,
  endDate: Dayjs,
  activeDateGroup?: any
) => {
  let dayJsUnit: 's' | 'm' | 'h' | 'd' | 'M' | 'w' | 'Q' | 'y';
  let activeDayJsUnit: 's' | 'm' | 'h' | 'd' | 'M' | 'w' | 'Q' | 'y';
  let yearMultiplier = 1;

  switch (dateGroup) {
    case DATE_TIME_GROUP.SECONDS:
      dayJsUnit = 's';
      break;
    case DATE_TIME_GROUP.MINUTES:
      dayJsUnit = 'm';
      break;
    case DATE_TIME_GROUP.HOURS:
      dayJsUnit = 'h';
      break;
    case DATE_TIME_GROUP.DAYS:
      dayJsUnit = 'd';
      break;
    case DATE_TIME_GROUP.MONTHS:
      dayJsUnit = 'M';
      break;
    case DATE_TIME_GROUP.WEEKS:
      dayJsUnit = 'w';
      break;
    case DATE_TIME_GROUP.QUARTERS:
      dayJsUnit = 'Q';
      break;
    case DATE_TIME_GROUP.YEARS:
      dayJsUnit = 'y';
      break;
    case DATE_TIME_GROUP.DECADES:
      dayJsUnit = 'y';
      yearMultiplier = 10;
      break;
    case DATE_TIME_GROUP.CENTURIES:
      dayJsUnit = 'y';
      yearMultiplier = 100;
      break;
    default:
      dayJsUnit = 'y';
  }

  switch (activeDateGroup) {
    case DATE_TIME_GROUP.SECONDS:
      activeDayJsUnit = 's';
      break;
    case DATE_TIME_GROUP.MINUTES:
      activeDayJsUnit = 'm';
      break;
    case DATE_TIME_GROUP.HOURS:
      activeDayJsUnit = 'h';
      break;
    case DATE_TIME_GROUP.DAYS:
      activeDayJsUnit = 'd';
      break;
    case DATE_TIME_GROUP.MONTHS:
      activeDayJsUnit = 'M';
      break;
    case DATE_TIME_GROUP.WEEKS:
      activeDayJsUnit = 'w';
      break;
    case DATE_TIME_GROUP.QUARTERS:
      activeDayJsUnit = 'Q';
      break;
    case DATE_TIME_GROUP.YEARS:
      activeDayJsUnit = 'y';
      break;
    case DATE_TIME_GROUP.DECADES:
      activeDayJsUnit = 'y';
      break;
    case DATE_TIME_GROUP.CENTURIES:
      activeDayJsUnit = 'y';
      break;
    default:
      activeDayJsUnit = 'y';
  }

  if (startDate.isSame(endDate)) {
    const groupStartingDate = startDate;
    let groupEndingDate;

    switch (activeDateGroup) {
      case DATE_TIME_GROUP.CENTURIES:
        groupEndingDate = groupStartingDate.add(100, 'y');
        break;
      case DATE_TIME_GROUP.DECADES:
        groupEndingDate = groupStartingDate.add(10, 'y');
        break;
      default:
        groupEndingDate = endDate.add(1, activeDayJsUnit as ManipulateType);
    }

    return groupEndingDate.diff(groupStartingDate, dayJsUnit);
  }

  return endDate.diff(startDate, dayJsUnit) / yearMultiplier;
};

export const getStartOfDateGroup = (dateGroup: any, date: Dayjs) => {
  const dayJsString = getDayJsUnitString(dateGroup);

  switch (dateGroup) {
    case DATE_TIME_GROUP.CENTURIES:
      return getStartOfCentury(date.year());
    case DATE_TIME_GROUP.DECADES:
      return getStartOfDecade(date.year());
    default:
      return dayJsString ? date.startOf(dayJsString) : date;
  }
};

export const getEndOfDateGroup = (dateGroup: any, date: Dayjs) => {
  const dayJsString = getDayJsUnitString(dateGroup);

  switch (dateGroup) {
    case DATE_TIME_GROUP.CENTURIES:
      return getStartOfCentury(date.year()).add(100, 'years');
    case DATE_TIME_GROUP.DECADES:
      return getStartOfDecade(date.year()).add(10, 'years');
    default:
      return dayJsString ? date.endOf(dayJsString) : date;
  }
};

export const getDayJsUnitString = (dateGroup: any) => {
  if (![DATE_GROUP.CENTURIES, DATE_GROUP.DECADES].includes(dateGroup))
    switch (dateGroup) {
      case DATE_TIME_GROUP.SECONDS:
        return 's';
      case DATE_TIME_GROUP.MINUTES:
        return 'm';
      case DATE_TIME_GROUP.HOURS:
        return 'h';
      case DATE_TIME_GROUP.DAYS:
        return 'd';
      case DATE_TIME_GROUP.MONTHS:
        return 'M';
      case DATE_TIME_GROUP.WEEKS:
        return 'w';
      case DATE_TIME_GROUP.QUARTERS:
        return 'Q';
      case DATE_TIME_GROUP.YEARS:
        return 'y';
    }
};
export const getYearMonthFormat = (dateGroup: any) => {
  switch (dateGroup) {
    case DATE_GROUP.WEEKS:
      return {
        // eslint-disable-next-line no-useless-escape
        week: "'WK ' ww-yyyy"
      };
    case DATE_GROUP.QUARTERS:
      return {
        month: 'Qq-yyyy',
        year: 'yyyy'
      };
    case DATE_GROUP.DECADES:
      return {
        month: 'MM-yyyy',
        // eslint-disable-next-line no-useless-escape
        year: `yyyy\''\'s`
      };
    case DATE_GROUP.CENTURIES:
      return {
        month: 'MM-yyyy',
        // eslint-disable-next-line no-useless-escape
        year: `yyyy\''\'s`
      };
  }

  return {
    month: 'MMM-yyyy',
    year: 'yyyy'
  };
};

export const getDateFormat = (dateGroup: any) => {
  switch (dateGroup) {
    case DATE_TIME_GROUP.SECONDS:
      return 'MMM D HH:mm:ss';
    case DATE_TIME_GROUP.MINUTES:
      return 'MMM D HH:mm';
    case DATE_TIME_GROUP.HOURS:
      return 'MMM D HH:00';
    case DATE_TIME_GROUP.DAYS:
      return 'll';
    case DATE_TIME_GROUP.WEEKS:
      return '[WK] WW-YYYY';
    case DATE_TIME_GROUP.MONTHS:
      return 'MMM-YYYY';
    case DATE_TIME_GROUP.QUARTERS:
      return '[Q]Q-YYYY';
    case DATE_TIME_GROUP.YEARS:
      return 'YYYY';
    case DATE_TIME_GROUP.DECADES:
      return `YYYY['s]`;
    case DATE_TIME_GROUP.CENTURIES:
      return `YYYY['s]`;
    case DATE_TIME_GROUP.DAYOFWEEK:
      return '';
    case DATE_TIME_GROUP.HOUROFDAY:
      return '';
    case DATE_TIME_GROUP.MONTHOFYEAR:
      return '';
    case DATE_TIME_GROUP.WEEKOFYEAR:
      return '';
    case DATE_TIME_GROUP.DAYOFMONTH:
      return '';

    default:
      return 'll';
  }
};

export const getDateFormatForTooltip = (dateGroup: any, isPie = false) => {
  switch (dateGroup) {
    case DATE_TIME_GROUP.SECONDS:
      return `{${isPie ? 'category' : 'dateX'}.formatDate('dd MMM HH:mm:ss')}`;
    case DATE_TIME_GROUP.MINUTES:
      return `{${isPie ? 'category' : 'dateX'}.formatDate('dd MMM HH:mm')}`;
    case DATE_TIME_GROUP.HOURS:
      return `{${isPie ? 'category' : 'dateX'}.formatDate('dd MMM HH:00')}`;
    case DATE_GROUP.DAYS:
      return `{${isPie ? 'category' : 'dateX'}.formatDate('dd-MM-yyyy')}`;
    case DATE_GROUP.WEEKS:
      return `WK {${isPie ? 'category' : 'dateX'}.formatDate('ww-yyyy')}`;
    case DATE_GROUP.MONTHS:
      return `{${isPie ? 'category' : 'dateX'}.formatDate('MMM-YYYY')}`;
    case DATE_GROUP.QUARTERS:
      return `{${isPie ? 'category' : 'dateX'}.formatDate('Qq-YYYY')}`;
    case DATE_GROUP.YEARS:
      return `{${isPie ? 'category' : 'dateX'}.formatDate('YYYY')}`;
    case DATE_GROUP.DECADES:
      return `{${isPie ? 'category' : 'dateX'}.formatDate('YYYY')}'s`;
    case DATE_GROUP.CENTURIES:
      return `{${isPie ? 'category' : 'dateX'}.formatDate('YYYY')}'s`;
    case DATE_GROUP.DAYOFWEEK || DATE_TIME_GROUP.DAYOFWEEK:
      return `{categoryX}`;
    case DATE_GROUP.DAYOFMONTH || DATE_TIME_GROUP.DAYOFMONTH:
      return `Day {categoryX}`;
    case DATE_GROUP.MONTHOFYEAR || DATE_TIME_GROUP.MONTHOFYEAR:
      return `{categoryX}`;
    case DATE_GROUP.WEEKOFYEAR || DATE_TIME_GROUP.WEEKOFYEAR:
      return `{categoryX}`;
    case DATE_TIME_GROUP.HOUROFDAY:
      return `{${isPie ? 'category' : 'dateX'}.formatDate('HH:00')}`;
    default:
      return `{${isPie ? 'category' : 'dateX'}.formatDate('dd-MM-yyyy')}`;
  }
};

export const getXAxisLabelsExcluded = ({
  isDate,
  isDateGroupChanged,
  dateRange,
  data,
  xAxisField,
  setDateGroupChanged
}: {
  isDate?: boolean;
  isDateGroupChanged: boolean;
  dateRange?: DateRange | null;
  data: any[];
  xAxisField: any;
  setDateGroupChanged: Dispatch<SetStateAction<boolean>>;
}) => {
  if (isDate && isDateGroupChanged) {
    let startInd = 0;
    let endInd = data?.length - 1;

    if (dateRange)
      data.forEach((item: any, index: number) => {
        if (
          (dayjs(dateRange?.startingDate).isBetween(
            item[xAxisField],
            data[index + 1]?.[xAxisField]
          ) ||
            dayjs(dateRange?.startingDate).isSame(item[xAxisField])) &&
          data[index + 1]
        )
          startInd = index;

        if (
          (dayjs(dateRange?.endingDate).isBetween(
            item[xAxisField],
            data[index + 1]?.[xAxisField]
          ) ||
            dayjs(dateRange?.endingDate).isSame(item[xAxisField])) &&
          data[index + 1]
        )
          endInd = index;
      });

    const xAxisLabels: any[] = [...data];

    if (startInd === endInd) xAxisLabels.splice(startInd, 1);
    else xAxisLabels.splice(startInd, endInd - startInd + 1);

    const xAxisLabelsExcluded = xAxisLabels.map((item) => item[xAxisField]);

    setDateGroupChanged(false);

    return xAxisLabelsExcluded || [];
  }
};

export const getChartTheme = (chartConfigData: ChartConfigData | DataState) => {
  const {
    config: { visual_settings },
    colorset,
    project_colorset
  } = chartConfigData;

  if (!visual_settings?.theme_connection_status)
    return project_colorset || null;

  return visual_settings?.theme_connection_status?.colorThemeToggle
    ? project_colorset || null
    : colorset || null;
};

export const reOrderChartTypes = (options: any[]) => {
  const validOptions = [];
  const invalidOptions = [];

  for (const option of options)
    if (option.disabled === false) validOptions.push(option);
    else invalidOptions.push(option);

  return validOptions.concat(invalidOptions);
};

export const getDropDownValueDataType = (options: any, selectedValue: any) => {
  const value = options.filter((option: any) => option.value === selectedValue);

  return value[0]?.type;
};

const getColumnType = (columnName: string, availableFields: any[]) => {
  if (availableFields) {
    const column = availableFields.find((obj: any) => obj.label === columnName);

    return column?.type;
  }
};

const getDateString = (date: any) =>
  dayjs(date).format(getDateFormat(DATE_TIME_GROUP.DAYS));

export const getFilterTooltipText = (
  filters: {
    [key: string]: ColumnItemRange | ColumnItemList;
  },
  dynamic_filters: {
    [key: string]: ColumnItemDynamicFilter;
  },
  availableFields: any[],
  isCalender?: boolean,
  topBottomText?: string,
  dateRange?: { [key: string]: any }
) => {
  const chartFilterArray = Object.entries(filters);

  const chartDynamicFilterArray = Object.entries(dynamic_filters);

  const activeChartFilters = chartFilterArray
    .map(([key, obj]: [string, ColumnItemRange | ColumnItemList]) => {
      if (obj.active === 'true') return key;
    })
    .filter(
      (value: string | undefined): value is string => value !== undefined
    );

  const activeChartDynamicFilters = chartDynamicFilterArray
    .map(([key, obj]: [string, ColumnItemDynamicFilter]) => {
      if (obj.active === 'true') return key;
    })
    .filter(
      (value: string | undefined): value is string => value !== undefined
    );

  const filterText =
    'Filter: ' +
    (typeof topBottomText === 'string' ? topBottomText : '') +
    (topBottomText &&
    (activeChartFilters?.length || activeChartDynamicFilters?.length)
      ? ','
      : '');

  let chartFilterText = '';

  if (isCalender)
    chartFilterText = `${activeChartFilters
      .filter(
        (columnName: string) =>
          getColumnType(columnName, availableFields) === 'DATE' ||
          getColumnType(columnName, availableFields) === 'DATETIME'
      )
      .map((columnName: string) => {
        const currentFilter: any = filters[columnName];

        return ` ${columnName} (Included: 
             ${getDateString(currentFilter.startValue)} to ${getDateString(
          currentFilter.endValue
        )})`;
      })
      .join(',')}`;
  else
    chartFilterText = `${activeChartFilters
      .map((columnName: string) => {
        const currentFilter = filters[columnName];

        return ` ${columnName} (Included: ${
          currentFilter.type === FilterType.VALUES
            ? `${currentFilter.values.length} values`
            : currentFilter.type === FilterType.RANGE &&
              (getColumnType(columnName, availableFields) === 'DATE' ||
                getColumnType(columnName, availableFields) === 'DATETIME')
            ? `${getDateString(currentFilter.startValue)} to ${getDateString(
                currentFilter.endValue
              )}`
            : `${currentFilter.startValue.toLocaleString()} to ${currentFilter.endValue.toLocaleString()}`
        })`;
      })
      .join(',')}`;

  const chartDynamicFilterText = `${activeChartDynamicFilters
    .map((columnName: string) => {
      const currentFilter: ColumnItemDynamicFilter =
        dynamic_filters[columnName];

      const isHourFilter = currentFilter.group_choice === 'hour';

      let startDate: any = '';
      let endDate: any = '';

      if (dateRange && Object.keys(dateRange).length) {
        startDate = dateRange[columnName]?.start_date;
        endDate = dateRange[columnName]?.end_date;
      }

      if (currentFilter.filter === DynamicFilterTypes.LAST_FULL)
        return ` ${columnName} (Last Full ${currentFilter.number_of} ${
          currentFilter.group_choice
        }${currentFilter.number_of > 1 ? 's' : ''}${
          startDate && endDate
            ? `, from ${
                isHourFilter ? getUTCTime(startDate) : getDateString(startDate)
              } to ${
                isHourFilter ? getUTCTime(endDate) : getDateString(endDate)
              }`
            : ''
        })`;
      else
        return ` ${columnName} (Current ${currentFilter.number_of} ${
          currentFilter.group_choice
        }${
          startDate && endDate
            ? `, from ${
                isHourFilter ? getUTCTime(startDate) : getDateString(startDate)
              } to ${
                isHourFilter ? getUTCTime(endDate) : getDateString(endDate)
              }`
            : ''
        })`;
    })
    .join(',')}`;

  if (
    !activeChartFilters?.length &&
    !activeChartDynamicFilters?.length &&
    !topBottomText?.length
  )
    return '';

  return (
    filterText +
    chartFilterText +
    `${
      chartDynamicFilterText.trim().length && chartFilterText.trim().length
        ? ','
        : ''
    }` +
    chartDynamicFilterText
  );
};

am4core.options.queue = true;
am4core.options.autoDispose = true;

export { am4core };
